//Built-in libraries

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createCategoryV2 } from '@/api/category';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import { useProgramCategories, useProgramCategoryType } from '@/hooks/ReactQueryHooks/useCategoryQuery';
//Services
import { postWorkspace, putWorkspace } from '../../api/Terminal/Workspace';
//Assets andd Miscellaneous
import { ReactQueryKey } from '../../constants/ReactQueryKeys';
import { notifyUser } from '../../store/Toast';
import type { Category } from '../../types/Category';
import type { IWorkspace } from '../../types/Workspace';
import { sanitizeResponseError } from '../../utils';
//Custom components
import Button from '../UI/Button';
import { HookForm } from '../UI/HookForm/Form';
import { HookInput } from '../UI/HookForm/Form/Input';
import { HookFormProvider } from '../UI/HookForm/Provider';

//Component Begin
interface IWorkspaceFormProps {
  handleSubmit: () => void;
  isWorkspaceListUnavailable?: boolean;
  onCancel: () => void;
  workspace: IWorkspace | undefined;
  workspaces?: IWorkspace[];
}
type ProgramCategoryOption = Pick<Category, 'id' | 'name'>;

const normalizeProgramName = (name: string) => name.trim().toLocaleLowerCase();
const formValueToString = (value: unknown) => String(value ?? '');
const slugifyProgramCategory = (name: string): string => {
  const normalizedName = name
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `program-${normalizedName || 'category'}-${Date.now().toString(36)}`.slice(0, 100);
};

const programNameExists = (name: string, workspaces: IWorkspace[], workspace?: IWorkspace) =>
  workspaces.some((item) => item.id !== workspace?.id && normalizeProgramName(item.name) === normalizeProgramName(name));

export const WorkspaceForm = ({
  workspace,
  workspaces = [],
  isWorkspaceListUnavailable = false,
  onCancel,
  handleSubmit,
}: IWorkspaceFormProps) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [serverError, setServerError] = useState<string>();
  const [categoryError, setCategoryError] = useState<string>();
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProgramCategoryOption | null>(workspace?.category ?? null);
  const nameInputId = useId();
  const descriptionInputId = useId();
  const categoryInputId = useId();
  const inlineCategoryNameId = useId();
  const inlineCategoryDescriptionId = useId();

  const programCategoryType = useProgramCategoryType();
  const programCategoryTypeId = programCategoryType.data?.data?.id;
  const programCategoriesQuery = useProgramCategories({ categoryTypeId: programCategoryTypeId });
  const programCategories = useMemo<ProgramCategoryOption[]>(() => {
    const categories =
      programCategoriesQuery.data?.map((category) => ({
        id: category.id,
        name: category.name,
      })) ?? [];
    if (!selectedCategory || categories.some((category) => category.id === selectedCategory.id)) return categories;
    return [...categories, selectedCategory];
  }, [programCategoriesQuery.data, selectedCategory]);

  const createWorkspace = useMutation((data: IPostWorkspacePayload) => postWorkspace({ data }), {
    onSuccess: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Program Successfully Created.',
          toastType: 'success',
        })
      );
      queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);
      handleSubmit();
    },
    onError: (error: unknown) => {
      const resError = sanitizeResponseError(error);
      setServerError(resError);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `${resError}`,
          toastType: 'failed',
        })
      );
    },
  });
  const updateWorkspace = useMutation((data: IPutWorkspacePayload) => putWorkspace({ data, workspaceId: Number(workspace?.id) }), {
    onSuccess: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Program Updated Successfully.',
          toastType: 'success',
        })
      );
      queryClient.invalidateQueries([ReactQueryKey.GET_WORKSPACES]);
      handleSubmit();
    },
    onError: (error: unknown) => {
      const resError = sanitizeResponseError(error);
      setServerError(resError);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });
  const createProgramCategory = useMutation(
    (data: { name: string; description: string; categoryTypeId: number }) =>
      createCategoryV2({
        name: data.name,
        description: data.description,
        categoryTypeId: data.categoryTypeId,
        slug: slugifyProgramCategory(data.name),
      }),
    {
      onSuccess: (category) => {
        const createdCategory = { id: category.id, name: category.name };
        setSelectedCategory(createdCategory);
        setCategoryName('');
        setCategoryDescription('');
        setCategoryError(undefined);
        setShowCategoryForm(false);
        queryClient.invalidateQueries([ReactQueryKey.GET_CATEGORIES]);
        queryClient.invalidateQueries([ReactQueryKey.GET_CATEGORIES, 'program', programCategoryTypeId]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Category added successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: unknown) => {
        const resError = sanitizeResponseError(error);
        setCategoryError(resError);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const onSubmit = (data: IWorkspace) => {
    if (createWorkspace.isLoading || updateWorkspace.isLoading) return;
    setServerError(undefined);
    const categoryId = selectedCategory?.id ?? null;
    const payload: IPostWorkspacePayload = {
      name: data.name.trim(),
      description: data.description?.trim(),
      ...(categoryId !== null || workspace ? { categoryId } : {}),
    };

    if (workspace) {
      const updatedWorkspace: IPutWorkspacePayload = {
        ...payload,
      };
      updateWorkspace.mutate(updatedWorkspace);
    } else {
      createWorkspace.mutate(payload);
    }
  };
  const isSubmitting = createWorkspace.isLoading || updateWorkspace.isLoading;
  const isCategoryLoading = programCategoryType.isLoading || programCategoriesQuery.isLoading || programCategoriesQuery.isFetching;
  const hasCategoryLoadError = programCategoryType.isError || programCategoriesQuery.isError;
  const isCreatingCategory = createProgramCategory.isLoading;
  const canCreateCategory = Boolean(programCategoryTypeId) && categoryName.trim().length >= 2 && categoryName.trim().length <= 50;

  const submitCategory = () => {
    if (!programCategoryTypeId || !canCreateCategory || isCreatingCategory) return;
    setCategoryError(undefined);
    createProgramCategory.mutate({
      name: categoryName.trim(),
      description: categoryDescription.trim(),
      categoryTypeId: programCategoryTypeId,
    });
  };

  return (
    <HookFormProvider defaultValues={{ name: '', description: '', ...workspace }}>
      <h3 className='mb-2 text-lg text-primaryColor-normal'>{workspace ? 'Update Program' : 'Create New Program'}</h3>
      <HookForm onSubmit={onSubmit} className='space-y-5'>
        <HookInput
          type='text'
          placeholder='Program Name'
          required
          label={'Name'}
          name='name'
          className='flex-grow'
          id={nameInputId}
          maxLength={50}
          validate={(value: unknown) => {
            const trimmedValue = formValueToString(value).trim();
            if (!trimmedValue) return 'Program name is required';
            if (trimmedValue.length > 50) return 'Program name must be 50 characters or fewer';
            if (programNameExists(trimmedValue, workspaces, workspace)) return 'A program with this name already exists.';
            return true;
          }}
        />
        <span className='text-xs'>50 Characters Maximum</span>
        <HookInput
          type='text'
          placeholder='Program Description'
          label='Description'
          className='flex-grow'
          name='description'
          id={descriptionInputId}
          maxLength={50}
          validate={(value: unknown) => {
            if (formValueToString(value).trim().length > 50) return 'Program description must be 50 characters or fewer';
            return true;
          }}
        />
        <span className='text-xs'>50 Characters Maximum</span>
        <div>
          <div className='flex items-end gap-2'>
            <div className='flex-1'>
              <ReactSelect<ProgramCategoryOption, false>
                id={categoryInputId}
                label='Category'
                value={selectedCategory}
                options={programCategories}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id.toString()}
                isClearable
                isDisabled={isCategoryLoading || hasCategoryLoadError}
                isLoading={isCategoryLoading}
                noOptionsMessage={() => (hasCategoryLoadError ? 'Unable to load categories' : 'No categories available')}
                placeholder={hasCategoryLoadError ? 'Unable to load categories' : 'Select Category'}
                onChange={(option) => setSelectedCategory(option)}
              />
            </div>
            <Button
              label='Add Category'
              btnStyle='btn-neutral'
              type='button'
              customClassName='mb-0 whitespace-nowrap'
              onClick={() => {
                setCategoryError(undefined);
                setShowCategoryForm((prev) => !prev);
              }}
              disabled={!programCategoryTypeId || programCategoryType.isLoading}
            />
          </div>
          {showCategoryForm && (
            <div className='mt-3 rounded border border-neutralColor-light bg-white p-3 space-y-3'>
              <div>
                <label htmlFor={inlineCategoryNameId} className='block text-sm font-medium text-neutralColor-dark'>
                  Category Name
                </label>
                <input
                  id={inlineCategoryNameId}
                  type='text'
                  className='form-input mt-1 w-full'
                  maxLength={50}
                  value={categoryName}
                  placeholder='Program category'
                  onChange={(event) => setCategoryName(event.target.value)}
                />
                <span className='text-xs'>50 Characters Maximum</span>
              </div>
              <div>
                <label htmlFor={inlineCategoryDescriptionId} className='block text-sm font-medium text-neutralColor-dark'>
                  Description
                </label>
                <textarea
                  id={inlineCategoryDescriptionId}
                  className='form-input mt-1 w-full'
                  maxLength={250}
                  value={categoryDescription}
                  placeholder='Category description'
                  onChange={(event) => setCategoryDescription(event.target.value)}
                />
                <span className='text-xs'>250 Characters Maximum</span>
              </div>
              {categoryError && (
                <p className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700' role='alert'>
                  {categoryError}
                </p>
              )}
              <div className='flex justify-end gap-2'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  type='button'
                  onClick={() => {
                    setShowCategoryForm(false);
                    setCategoryError(undefined);
                  }}
                  disabled={isCreatingCategory}
                />
                <Button
                  label={isCreatingCategory ? 'Adding...' : 'Add'}
                  btnStyle='btn-primary'
                  type='button'
                  onClick={submitCategory}
                  disabled={!canCreateCategory || isCreatingCategory}
                />
              </div>
            </div>
          )}
        </div>
        {isWorkspaceListUnavailable && (
          <p className='rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800' role='status'>
            Some existing programs could not be loaded. You can still submit; duplicate checks will be confirmed by the server.
          </p>
        )}
        {serverError && (
          <p className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700' role='alert'>
            {serverError}
          </p>
        )}
        <div className='flex justify-end mt-4 gap-2'>
          <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={onCancel} disabled={isSubmitting} />
          <Button
            label={isSubmitting ? 'Saving...' : workspace ? 'Save' : 'Create'}
            btnStyle='btn-primary'
            type='submit'
            disabled={isSubmitting}
          />
        </div>
      </HookForm>
    </HookFormProvider>
  );
};
